import React from "react";
import { getCurrentLang, localizePath } from "../i18n/core";

const SITE_URL = "https://livingfincacortesin.com";

// Finca Cortesín resort centroid (Casares, Málaga, Spain)
const RESORT_GEO = { lat: 36.3719, lng: -5.1622 };

const AGENT_ORG = {
  "@type": "RealEstateAgent",
  name: "Enova Estates",
  url: "https://enovaestates.com",
  email: "bjorn@enovaestates.com",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Manilva Beach Fase 2, Local 7",
    addressLocality: "Manilva",
    postalCode: "29692",
    addressRegion: "Málaga",
    addressCountry: "ES",
  },
};

export default function SchemaMarkup({ schema }) {
  if (!schema) return null;
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function buildBreadcrumb(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => {
      const entry = {
        "@type": "ListItem",
        position: i + 1,
        name: it.name,
      };
      // Google's canonical pattern: last item is the current page → omit item URL
      // to avoid "Invalid URL in field 'id'" warnings. Also strip query strings
      // so the URL matches the canonical path exactly.
      const isLast = i === items.length - 1;
      if (!isLast && it.url) {
        const raw = it.url.split("?")[0].split("#")[0];
        entry.item = raw.startsWith("http") ? raw : `${SITE_URL}${localizePath(raw)}`;
      }
      return entry;
    }),
  };
}

function stripHtml(s) {
  if (!s) return "";
  return String(s)
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 5000);
}

export function buildFAQ(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

// Parse "3 to 5" or "3-5" or "4" -> {min, max}
function parseRoomRange(s) {
  if (!s) return {};
  const nums = String(s).match(/\d+/g);
  if (!nums) return {};
  if (nums.length === 1) return { min: Number(nums[0]), max: Number(nums[0]) };
  return { min: Number(nums[0]), max: Number(nums[nums.length - 1]) };
}

// Parse "180-260 m²" -> {min, max, unit}
function parseFloorSize(s) {
  if (!s) return null;
  const nums = String(s).match(/\d+/g);
  if (!nums) return null;
  const min = Number(nums[0]);
  const max = Number(nums[nums.length - 1]);
  return { min, max };
}

export function buildRealEstateListing(d) {
  const url = `${SITE_URL}${localizePath(`/developments/${d.slug}`)}`;
  const rooms = parseRoomRange(d.bedrooms);
  const floor = parseFloorSize(d.builtSizes);
  const images = Array.isArray(d.gallery) && d.gallery.length > 0
    ? d.gallery.slice(0, 6)
    : [d.image].filter(Boolean);

  const listing = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    "@id": url,
    url,
    name: d.name,
    inLanguage: getCurrentLang(),
    headline: d.tagline || d.name,
    description: stripHtml(d.intro) || d.tagline || `${d.name} — new luxury development inside Finca Cortesín, Casares.`,
    image: images,
    datePosted: d.created_at || new Date().toISOString(),
    address: {
      "@type": "PostalAddress",
      streetAddress: d.location || "Finca Cortesín Resort",
      addressLocality: "Casares",
      addressRegion: "Málaga",
      postalCode: "29690",
      addressCountry: "ES",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: RESORT_GEO.lat,
      longitude: RESORT_GEO.lng,
    },
    broker: AGENT_ORG,
    additionalProperty: [
      d.completion && { "@type": "PropertyValue", name: "Completion", value: d.completion },
      d.developer && { "@type": "PropertyValue", name: "Developer", value: d.developer },
      d.units && { "@type": "PropertyValue", name: "Units", value: d.units },
      d.terraceSizes && { "@type": "PropertyValue", name: "Terrace sizes", value: d.terraceSizes },
      d.propertyType && { "@type": "PropertyValue", name: "Property type", value: d.propertyType },
      d.views && { "@type": "PropertyValue", name: "Views", value: d.views },
    ].filter(Boolean),
  };

  if (rooms.min !== undefined) {
    listing.numberOfRooms = {
      "@type": "QuantitativeValue",
      minValue: rooms.min,
      maxValue: rooms.max,
      unitText: "bedrooms",
    };
  }

  if (floor) {
    listing.floorSize = {
      "@type": "QuantitativeValue",
      minValue: floor.min,
      maxValue: floor.max,
      unitCode: "MTK", // square metres (UN/CEFACT)
    };
  }

  if (d.priceFromValue) {
    listing.offers = {
      "@type": "Offer",
      url,
      priceCurrency: "EUR",
      price: d.priceFromValue,
      priceSpecification: {
        "@type": "PriceSpecification",
        priceCurrency: "EUR",
        price: d.priceFromValue,
        valueAddedTaxIncluded: false,
        description: `From ${d.priceFrom || "€" + d.priceFromValue.toLocaleString()}`,
      },
      availability: "https://schema.org/InStock",
      seller: AGENT_ORG,
    };
  }

  if (Array.isArray(d.amenities) && d.amenities.length > 0) {
    listing.amenityFeature = d.amenities.slice(0, 12).map((a) => ({
      "@type": "LocationFeatureSpecification",
      name: typeof a === "string" ? a : a?.name || String(a),
      value: true,
    }));
  }

  return listing;
}

export function buildArticle(post) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    inLanguage: getCurrentLang(),
    headline: post.title,
    description: stripHtml(post.excerpt),
    image: post.image,
    datePublished: post.date,
    author: { "@type": "Organization", name: "Finca Cortesín New Developments" },
    publisher: AGENT_ORG,
  };
}

// Site-wide organisation schema (mount once on Home)
export function buildOrganization() {
  return {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    name: "Enova Estates",
    alternateName: "Finca Cortesín New Developments",
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    email: "bjorn@enovaestates.com",
    areaServed: {
      "@type": "Place",
      name: "Finca Cortesín, Casares, Costa del Sol, Spain",
    },
    address: AGENT_ORG.address,
    sameAs: ["https://enovaestates.com"],
  };
}

export function buildWebsite() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Living Finca Cortesín",
    url: `${SITE_URL}${localizePath("/")}`,
    inLanguage: getCurrentLang(),
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/developments?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}
